// src/components/pos/ShoppingCart.jsx
import React, { useRef, useEffect, useState } from 'react';
import { Trash2, Plus, Minus, BookmarkPlus, BookOpen, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { cashRemarkCategoriesApi, creditCustomersApi } from '../../api';

// 兜底 key，父组件没传 getItemKey 时用
const defaultItemKey = (item) =>
  `${item.product_id}-${item.sell_type || 'unit'}-${item.price_type || 'retail'}-${item.sku_id || 0}`;

// 到期状态：过期/临期（7天内）/正常——和 InventoryManagementPage.jsx 用的是同一套判断逻辑
function getExpiryStatus(expiryDate) {
  if (!expiryDate) return 'none';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 7) return 'soon';
  return 'ok';
}

// 批次分配面板：展示到期日期+计划扣多少，支持手动改数量，过期/临期的批次标红/标黄提醒。
// 不显示成本价——收银员不该看到这个，和VIP价对收银员不可见是同一个原则
const BatchAllocationPanel = ({ item, onUpdateBatchAllocation, itemKey }) => {
  const allocations = item.batchAllocations || [];
  const totalAllocated = allocations.reduce((sum, a) => sum + (parseInt(a.quantity) || 0), 0);
  const isValid = totalAllocated === item.quantity;

  const handleQtyChange = (batchId, newQty) => {
    const qty = Math.max(0, parseInt(newQty) || 0);
    const updated = allocations.map(a => a.batch_id === batchId ? { ...a, quantity: qty } : a);
    onUpdateBatchAllocation(itemKey, updated);
  };

  return (
    <div className="mt-2 pt-2 border-t border-dashed border-gray-200 space-y-2">
      <p className="text-xs text-gray-500">该商品涉及多个批次，系统已按到期日期自动分配，可手动调整：</p>
      {item.availableBatches.map(batch => {
        const alloc = allocations.find(a => a.batch_id === batch.id);
        const status = getExpiryStatus(batch.expiry_date);
        return (
          <div key={batch.id} className={`flex items-center justify-between text-xs rounded-lg px-2 py-1.5 ${
            status === 'expired' ? 'bg-red-50' : status === 'soon' ? 'bg-amber-50' : 'bg-gray-50'
          }`}>
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="text-gray-700">批次 {batch.batch_no}</span>
                {status === 'expired' && <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-medium">已过期</span>}
                {status === 'soon' && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">临期</span>}
              </div>
              <span className={status === 'expired' ? 'text-red-500' : status === 'soon' ? 'text-amber-600' : 'text-gray-400'}>
                {batch.expiry_date ? `到期：${batch.expiry_date}` : '无到期日期'} · 剩余 {batch.quantity} 个
              </span>
            </div>
            <input
              type="number" min="0" max={batch.quantity}
              value={alloc ? alloc.quantity : 0}
              onChange={e => handleQtyChange(batch.id, e.target.value)}
              className="w-16 px-1.5 py-1 border border-gray-300 rounded text-center text-xs"
            />
          </div>
        );
      })}
      {!isValid && (
        <p className="text-xs text-red-500">
          ⚠️ 已分配 {totalAllocated} 个，需要 {item.quantity} 个，请调整至一致才能结账
        </p>
      )}
    </div>
  );
};

const ShoppingCart = ({
  cartItems = [],
  getItemKey = defaultItemKey,
  onUpdateQuantity,
  onRemoveItem,
  onUpdateBatchAllocation,
  onCheckout,
  onHold,
  onOpenPending,
  pendingCount = 0,
  paymentMethod = 'cash',
  onPaymentMethodChange,
  // 结账方式开关——由父组件(POSPage.jsx)从商家设置里读出来传进来，
  // 默认都给true兜底，避免设置还没加载完之前，所有支付方式突然全部消失
  posEnableCash = true,
  posEnableCard = true,
  posEnableMobile = true,
  posEnableCredit = false,
  // 现金备注分类 / 记账客户——选中的值和变更回调都交给父组件持有，
  // 和 paymentMethod 是同一种"父组件受控"的模式，方便父组件在真正提交订单时
  // 把这两个值一起塞进请求里
  cashRemarkCategoryId = '',
  onCashRemarkCategoryChange,
  creditCustomerId = '',
  onCreditCustomerChange
}) => {
  const { language } = useApp();
  const t = translations[language];
  const containerRef = useRef(null);
  const [cartListHeight, setCartListHeight] = useState(300);
  const [expandedBatchKey, setExpandedBatchKey] = useState(null); // 当前展开批次面板的那一行

  const [cashCategories, setCashCategories] = useState([]);
  const [creditCustomers, setCreditCustomers] = useState([]);

  // 现金备注分类——只在选了"现金"这个支付方式的时候才去加载，不用一进收银台
  // 就查这个几乎用不上的列表
  useEffect(() => {
    if (paymentMethod === 'cash' && posEnableCash) {
      cashRemarkCategoriesApi.getList({ status: 'active' })
        .then(res => { if (res.success) setCashCategories(res.data || []); })
        .catch(err => console.error('加载现金备注分类失败:', err));
    }
  }, [paymentMethod, posEnableCash]);

  // 记账客户列表——只在选了"记账未收款"的时候才加载
  useEffect(() => {
    if (paymentMethod === 'credit' && posEnableCredit) {
      creditCustomersApi.getList({ status: 'active' })
        .then(res => { if (res.success) setCreditCustomers(res.data || []); })
        .catch(err => console.error('加载记账客户失败:', err));
    }
  }, [paymentMethod, posEnableCredit]);

  const getDisplayName = (item) => {
    if (language === 'en') return item.product_name_en || item.product_name;
    if (language === 'km') return item.product_name_km || item.product_name;
    return item.product_name;
  };

  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        const fixedHeight = 60 + 80 + 180 + 60 + 40;
        setCartListHeight(Math.max(containerHeight - fixedHeight, 200));
      }
    };
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const totalAmount = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 0), 0)
    : 0;

  const totalItems = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
    : 0;

  // 结账前的批次分配是否都合法（每个多批次商品的分配总数都要等于其数量）
  const hasInvalidBatchAllocation = Array.isArray(cartItems) && cartItems.some(item => {
    if (!item.availableBatches || item.availableBatches.length <= 1) return false;
    const total = (item.batchAllocations || []).reduce((s, a) => s + (parseInt(a.quantity) || 0), 0);
    return total !== item.quantity;
  });

  // 选了"记账未收款"，必须选一个记账客户才能结账——这一条和批次分配一样，
  // 都是"结账按钮要不要禁用"的判断条件之一
  const creditMissingCustomer = paymentMethod === 'credit' && !creditCustomerId;

  const getSellTypeTag = (item) => {
    if (item.sell_type === 'box') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
          📦 整箱
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
        散装
      </span>
    );
  };

  const getPriceTypeTag = (item) => {
    if (item.price_type === 'vip') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
          VIP
        </span>
      );
    }
    return null;
  };

  // 规格标签：有 sku_label 才显示
  const getSkuTag = (item) => {
    if (!item.sku_label) return null;
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">
        {item.sku_label}
      </span>
    );
  };

  // 多批次提示标签——只在这个商品当前确实有2个及以上批次时出现，
  // 平时单批次的商品不显示这个，界面不会被无关的提示搞乱
  const getBatchTag = (item, key) => {
    if (!item.availableBatches || item.availableBatches.length <= 1) return null;
    const isExpanded = expandedBatchKey === key;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpandedBatchKey(isExpanded ? null : key); }}
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200"
      >
        <Layers className="w-3 h-3" />
        {item.availableBatches.length}批次
        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
    );
  };

  const getUnitLabel = (item) => {
    if (item.sell_type === 'box') {
      return `$${(item.unit_price || 0).toFixed(2)}/箱 × ${item.quantity}箱`;
    }
    return `$${(item.unit_price || 0).toFixed(2)}/个 × ${item.quantity}个`;
  };

  // 支付方式列表——按商家设置里开启的那几种过滤，挂单不算支付方式，不受这组开关影响，
  // 一直可用（渲染在下面单独的挂单按钮里，不在这个列表里）
  const paymentOptions = [
    { value: 'cash',   label: t.cash,   enabled: posEnableCash },
    { value: 'card',   label: t.card,   enabled: posEnableCard },
    { value: 'mobile', label: t.mobile, enabled: posEnableMobile },
    { value: 'credit', label: '记账未收款', enabled: posEnableCredit },
  ].filter(opt => opt.enabled);

  return (
    <div ref={containerRef} className="bg-white rounded-xl shadow-lg p-6 h-full flex flex-col">

      {/* 标题 + 挂单列表按钮 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold">{t.cart}</h3>
        <button
          onClick={onOpenPending}
          className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors">
          <BookOpen className="w-4 h-4" />
          <span>挂单</span>
          {pendingCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* 购物车列表 */}
      <div className="overflow-y-auto mb-4" style={{ height: `${cartListHeight}px` }}>
        {!cartItems || cartItems.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <p>{t.cartIsEmpty || '购物车是空的'}</p>
            <p className="text-sm mt-2">
              {language === 'en' ? 'Please select products to add' :
               language === 'km' ? 'សូមជ្រើសរើសទំនិញដើម្បីបន្ថែម' :
               '请选择商品添加'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 pr-2">
            {cartItems.map((item) => {
              const key = getItemKey(item);
              return (
                <div key={key}
                  className="border rounded-lg p-3 hover:shadow-md transition-shadow">

                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 text-sm truncate">{getDisplayName(item)}</h4>
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {getSkuTag(item)}
                        {getSellTypeTag(item)}
                        {getPriceTypeTag(item)}
                        {getBatchTag(item, key)}
                      </div>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <p className="font-bold text-blue-600 text-sm">
                        ${((item.unit_price || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 mb-2">{getUnitLabel(item)}</p>

                  {expandedBatchKey === key && item.availableBatches && item.availableBatches.length > 1 && (
                    <BatchAllocationPanel
                      item={item}
                      itemKey={key}
                      onUpdateBatchAllocation={onUpdateBatchAllocation}
                    />
                  )}

                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(key, item.quantity - 1)}
                        className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-10 text-center font-medium text-sm">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(key, item.quantity + 1)}
                        className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => onRemoveItem && onRemoveItem(key)}
                      className="text-red-500 hover:text-red-700 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 底部固定区域 */}
      <div className="border-t pt-4 space-y-4 mt-auto">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">商品数量:</span>
            <span className="font-medium">{totalItems} 件</span>
          </div>
          <div className="flex justify-between items-center text-lg font-bold">
            <span>{t.total}:</span>
            <span className="text-blue-600">${totalAmount.toFixed(2)}</span>
          </div>
        </div>

        {/* 支付方式——只渲染商家设置里开启的那几种 */}
        <div className="border-t pt-3">
          <h4 className="font-semibold mb-2 text-sm">{t.paymentMethod}</h4>
          <div className="space-y-1">
            {paymentOptions.map(opt => (
              <label key={opt.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                <input type="radio" value={opt.value}
                  checked={paymentMethod === opt.value}
                  onChange={e => onPaymentMethodChange && onPaymentMethodChange(e.target.value)}
                  className="mr-2" />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>

          {/* 现金备注分类——选填，只在选中"现金"时出现 */}
          {paymentMethod === 'cash' && cashCategories.length > 0 && (
            <div className="mt-2">
              <select value={cashRemarkCategoryId}
                onChange={e => onCashRemarkCategoryChange && onCashRemarkCategoryChange(e.target.value)}
                className="w-full px-2 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">备注分类（选填）</option>
                {cashCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* 记账客户——必选，只在选中"记账未收款"时出现 */}
          {paymentMethod === 'credit' && (
            <div className="mt-2">
              <select value={creditCustomerId}
                onChange={e => onCreditCustomerChange && onCreditCustomerChange(e.target.value)}
                className={`w-full px-2 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 ${
                  creditMissingCustomer ? 'border-red-300' : ''
                }`}>
                <option value="">请选择记账客户（必选）</option>
                {creditCustomers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{parseFloat(c.unsettled_total) > 0 ? `（已欠 $${parseFloat(c.unsettled_total).toFixed(2)}）` : ''}
                  </option>
                ))}
              </select>
              {creditCustomers.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">还没有记账客户，请先去"记账客户管理"里新增</p>
              )}
            </div>
          )}
        </div>

        {hasInvalidBatchAllocation && (
          <p className="text-xs text-red-500 text-center">
            ⚠️ 有商品的批次分配数量不对，请先调整好再结账
          </p>
        )}
        {creditMissingCustomer && (
          <p className="text-xs text-red-500 text-center">
            ⚠️ 记账未收款，请先选择记账客户
          </p>
        )}

        {/* 结算 + 挂单 按钮 */}
        <div className="flex gap-2">
          <button
            onClick={onHold}
            disabled={!cartItems || cartItems.length === 0}
            className={`flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
              cartItems && cartItems.length > 0
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer'
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
            }`}>
            <BookmarkPlus className="w-4 h-4" />
            挂单
          </button>

          <button
            onClick={onCheckout}
            disabled={!cartItems || cartItems.length === 0 || hasInvalidBatchAllocation || creditMissingCustomer}
            className={`flex-1 py-3 rounded-lg font-semibold text-white transition-colors ${
              cartItems && cartItems.length > 0 && !hasInvalidBatchAllocation && !creditMissingCustomer
                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'bg-gray-300 cursor-not-allowed'
            }`}>
            {cartItems && cartItems.length > 0 ? t.checkout : '请先添加商品'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCart;